import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["rulesContainer", "rowTemplate", "emptyState"]
  static values = {
    url: String,
    cancelUrl: String,
    fieldKey: String,
    fieldKind: String,
    frameId: String,
    errorMessage: String,
  }

  connect() {
    this.refreshEmptyState()
    this.refreshValueVisibility()
  }

  addRule(event) {
    event.preventDefault()
    const fragment = this.rowTemplateTarget.content.cloneNode(true)
    const row = fragment.querySelector("[data-warnings-editor-target='row']")
    if (row) {
      row.dataset.ruleId = this.generateId()
    }
    this.rulesContainerTarget.appendChild(fragment)
    this.refreshEmptyState()
    this.refreshValueVisibility()
  }

  removeRule(event) {
    event.preventDefault()
    const row = event.target.closest("[data-warnings-editor-target='row']")
    if (row) {
      row.remove()
      this.refreshEmptyState()
    }
  }

  matcherChanged() {
    this.refreshValueVisibility()
  }

  refreshEmptyState() {
    if (!this.hasEmptyStateTarget) return
    const hasRows = this.rulesContainerTarget.querySelectorAll(
      "[data-warnings-editor-target='row']",
    ).length
    this.emptyStateTarget.classList.toggle("hidden", hasRows > 0)
  }

  refreshValueVisibility() {
    this.rulesContainerTarget
      .querySelectorAll("[data-warnings-editor-target='row']")
      .forEach((row) => {
        const matcher = row.querySelector("[data-field='matcher']")?.value
        const valueWrapper = row.querySelector("[data-warnings-editor-target='valueWrapper']")
        if (!valueWrapper) return
        const hidden = matcher === "is_empty" || matcher === "is_not_empty"
        valueWrapper.classList.toggle("hidden", hidden)
      })
  }

  async save(event) {
    event.preventDefault()
    const csrfToken = document.querySelector("[name='csrf-token']")?.content
    const body = { warnings: this.collectRules() }
    if (this.fieldKindValue === "core") body.field_key = this.fieldKeyValue

    try {
      const response = await fetch(this.urlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          Accept: "text/vnd.turbo-stream.html",
        },
        body: JSON.stringify(body),
      })

      if (!response.ok) throw new Error("Update failed")
      const html = await response.text()
      Turbo.renderStreamMessage(html)
    } catch (error) {
      console.error("Error saving warnings:", error)
      alert(this.errorMessageValue || "Error saving. Please try again.")
    }
  }

  cancel(event) {
    event.preventDefault()
    const frame = document.getElementById(this.frameIdValue)
    if (frame && this.cancelUrlValue) {
      frame.src = this.cancelUrlValue
      frame.reload()
    }
  }

  collectRules() {
    return Array.from(
      this.rulesContainerTarget.querySelectorAll("[data-warnings-editor-target='row']"),
    ).map((row) => {
      const matcher = row.querySelector("[data-field='matcher']")?.value || "is_empty"
      const value = row.querySelector("[data-field='value']")?.value || ""
      const message = row.querySelector("[data-field='message']")?.value || ""
      const enabled = row.querySelector("[data-field='enabled']")?.checked !== false
      const id = row.dataset.ruleId || this.generateId()
      const noValueNeeded = matcher === "is_empty" || matcher === "is_not_empty"
      return {
        id,
        matcher,
        value: noValueNeeded ? null : value,
        message: message || null,
        severity: "warning",
        enabled,
      }
    })
  }

  generateId() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
    return `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
  }
}
